import React, { FC, useContext, useMemo, useEffect, useRef } from 'react';
import qs from 'query-string';
import { useLocation } from 'react-router-dom';
import ChildIssuesIcon from '@atlaskit/icon/glyph/child-issues';
import { SloGroupCohortSummaryDTO, SloGroupDTO } from '@atlassiansox/phobos-client-ts';
import EmptyState from '@atlaskit/empty-state';
import cytoscape from 'cytoscape';
import dagre from 'cytoscape-dagre';
import { maxBy } from 'lodash';

import {
  breakWord,
  extendWidth,
  flex,
  flexDirectionColumn,
  flexStart,
  spaceGapMinimum,
  spaceGapRelaxed,
  spaceTopCozy,
} from '@/styles';
import { StageDisplayContext, StageDisplayType } from '@/controllers/stage-display-controller';
import { convertCommaSeparatedToQueryParams } from '@/utils';
import { ThemeContext } from '@/controllers/theme-controller';

import { sloGroupSearchObjectType } from '../types';
import { dagGraphContainerStyles, stickyHeadStyles } from '../styles';
import { cohortHierarchyToBFSData, createCytoscapeData, setupCytoscapeInstance } from '../utils';
import { getCytoscapeConfig } from '../constants';

import FilterBarDynamic from './filter-bar-dynamic';
import SLOCohortDynamic from './slo-cohort-dynamic';

// Register dagre layout extension
cytoscape.use(dagre);

type SLOPanelDynamicProps = {
  data: SloGroupDTO;
};

const SLOPanelDynamic: FC<SLOPanelDynamicProps> = ({ data: sloGroupData }) => {
  // cohortHierarchy gives upper cased cohort types in a tree structure now
  const { publicId, name, cohortHierarchy } = sloGroupData;
  const [theme] = useContext(ThemeContext);
  // Transform cohortHierarchy to breadth-first traversal (BFS) array of cohort
  const cohortTypeSorted: string[] = useMemo(
    () => (cohortHierarchy ? cohortHierarchyToBFSData(cohortHierarchy) : []),
    [cohortHierarchy]
  );

  // Transform cohortHierarchy data to cytoscape format
  const cytoscapeData = useMemo(() => createCytoscapeData(cohortHierarchy), [cohortHierarchy]);
  const cyRef = useRef<HTMLDivElement>(null);
  const cyInstance = useRef<cytoscape.Core | null>(null);

  useEffect(() => {
    if (cyRef.current && cytoscapeData) {
      const maxNodeLabelLength = maxBy(cytoscapeData.nodes, node => node.data.label.length)?.data
        .label?.length;
      cyInstance.current = cytoscape({
        container: cyRef.current,
        elements: [...cytoscapeData.nodes, ...cytoscapeData.edges],
        ...getCytoscapeConfig(theme, maxNodeLabelLength),
      });

      cyInstance.current.ready(() => {
        if (cyInstance.current && cyRef.current) {
          setupCytoscapeInstance(cyInstance.current, cyRef.current);
        }
      });
    }

    return () => {
      if (cyInstance.current) {
        cyInstance.current.destroy();
      }
    };
  }, [cytoscapeData, theme]);

  const { stageDisplayType } = useContext(StageDisplayContext);
  const isFullScreen = stageDisplayType === StageDisplayType.FullScreen;

  const { search } = useLocation();
  const { hasBreachStatusOnly, hideDrafts, cohorts } = useMemo(() => {
    const parsedSearch = qs.parse(search);
    const cohortsMap = new Map<string, string>();
    cohortTypeSorted.forEach((cohort: string) => {
      cohortsMap.set(
        cohort,
        convertCommaSeparatedToQueryParams((parsedSearch[cohort] as string) ?? '', cohort)
      );
    });

    const {
      pageBase,
      pageRollup,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      customFilters,
      ...restFilters
    }: sloGroupSearchObjectType = parsedSearch;
    return {
      hasBreachStatusOnly: parsedSearch.hasBreachStatusOnly as string | undefined,
      hideDrafts: parsedSearch.hideDrafts,
      currentPageBase: parseInt(pageBase || '1', 10),
      currentPageRollup: parseInt(pageRollup || '1', 10),
      searchFilters: qs.stringify(restFilters),
      cohorts: cohortsMap,
    };
  }, [search, cohortTypeSorted]);

  const sloGroupCohortSummaries: SloGroupCohortSummaryDTO[] =
    sloGroupData?.sloGroupSummary?.sloGroupCohortSummaries || [];
  const hasCohorts: boolean = sloGroupCohortSummaries.length > 0;

  if (!hasCohorts) {
    return (
      <EmptyState
        header=""
        description="No cohorts or SLOs found for this SLO group"
        imageUrl="/assets/not-found.svg"
      />
    );
  }

  return (
    <div css={extendWidth}>
      <div css={isFullScreen ? stickyHeadStyles : ''}>
        {isFullScreen && (
          <div css={[breakWord]}>
            <h4 css={[flexStart, spaceGapMinimum]}>
              <ChildIssuesIcon label="SLO Group Icon" size="small" />
              {name}
            </h4>
          </div>
        )}
        <FilterBarDynamic publicId={publicId} cohortTypeSorted={cohortTypeSorted} />
      </div>
      <div css={[spaceTopCozy, dagGraphContainerStyles]} ref={cyRef} />
      <div css={[spaceTopCozy, flexDirectionColumn, flex, spaceGapRelaxed]}>
        {cohortTypeSorted.map((cohort: string, index: number) => {
          const sloGroupCohortSummary = sloGroupCohortSummaries.find(x => x.cohort === cohort);
          if (sloGroupCohortSummary && (!hideDrafts || !sloGroupCohortSummary.draft)) {
            return (
              <SLOCohortDynamic
                isRootCohort={index === 0}
                key={`${publicId}:${cohort}`}
                hasBreachStatusOnly={hasBreachStatusOnly}
                sloGroupCohortSummary={sloGroupCohortSummary}
                publicId={publicId}
                cohorts={cohorts}
                cohortTypeSorted={cohortTypeSorted}
              />
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default SLOPanelDynamic;
